import { useCustom, type HttpError } from "@refinedev/core";
import type { LifecycleInfo } from "../types";

interface UseLifecycleOptions {
  itemType: string;
  itemId: string;
  enabled?: boolean;
}

interface UseLifecycleResult {
  lifecycle: LifecycleInfo | null;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
  refetch: () => void;
}

/**
 * Hook to fetch lifecycle information for an item.
 * Uses the generic items endpoint which handles all item types.
 *
 * @param itemType - The exact Aras ItemType name (e.g., "Lead", "Opportunity")
 * @param itemId - The item's ID
 * @param enabled - Whether to enable the fetch
 */
export const useLifecycle = ({
  itemType,
  itemId,
  enabled = true,
}: UseLifecycleOptions): UseLifecycleResult => {
  const shouldFetch = enabled && !!itemType && !!itemId;

  const {
    data: lifecycleData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCustom<LifecycleInfo>({
    // Use exact itemType name - no conversion
    url: `/items/${itemType}/${itemId}/lifecycle`,
    method: "get",
    queryOptions: {
      enabled: shouldFetch,
      staleTime: 60 * 1000, // 1 minute cache
      retry: 1, // Only retry once for lifecycle
    },
  });

  return {
    lifecycle: lifecycleData?.data || null,
    isLoading: shouldFetch && isLoading,
    isError,
    error: error || null,
    refetch,
  };
};
